import ctl from '@netlify/classnames-template-literals'
import { motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'

export type AnimateChangeInHeightProps = {
	children: React.ReactNode
	className?: string
	duration?: number
	hideOverflow?: boolean
	ease?: 'easeInOut' | 'linear'

	// escape hatch for disabling dynamically
	animate?: boolean
}

export function AnimateChangeInHeight({ animate = true, ...props }: AnimateChangeInHeightProps) {
	return animate ? <AnimatedHeight {...props} /> : <StaticHeight {...props} />
}

function StaticHeight({ children, className, hideOverflow = true }: AnimateChangeInHeightProps) {
	return <div className={ctl(`${hideOverflow && 'overflow-hidden'} ${className}`)}>{children}</div>
}

function AnimatedHeight(
	{ children, className, duration = 0.1, hideOverflow = true, ease = 'easeInOut' }: AnimateChangeInHeightProps,
) {
	const containerRef = useRef<HTMLDivElement>(null)
	const [height, setHeight] = useState<number | 'auto'>(0)

	useEffect(() => {
		const container = containerRef.current
		if (!container) { return }

		const resizeObserver = new ResizeObserver((entries) => {
			const observedHeight = entries[0].contentRect.height
			setHeight(observedHeight)
		})

		resizeObserver.observe(container)
		return () => resizeObserver.disconnect()
	}, [])

	return (
		// Animating height is a layout change, so it cannot go to the compositor the way transform
		// and opacity can; `contain-layout` keeps each frame's reflow inside this box. Not
		// `contain-paint` — callers pass `hideOverflow={false}` to let content escape the box.
		<motion.div
			className={ctl(`contain-layout ${hideOverflow && 'overflow-hidden'} ${className}`)}
			animate={{ height }}
			transition={{ duration, ease }}
		>
			<div ref={containerRef}>{children}</div>
		</motion.div>
	)
}
